import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { RouteLocationRaw } from "vue-router";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useServerErrorHandler } from "@verbleif/shared/src/composables/Error/serverErrorHandler";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { automationVoter, reportVoter, reservationVoter } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { ThemeSetting, useTheme } from "@verbleif/shared/src/composables/Theme/Theme";
import { chooseTheme } from "@verbleif/shared/src/composables/Theme/themePreference";
import { reloadSessionAfterIdentityChange } from "@web/core/reloadSessionAfterIdentityChange";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { version as pkgVersion } from "../../../package.json";
import { reportImpersonationStopped } from "../Auth/Impersonate/impersonation.api";
import { useImpersonationStore } from "../Auth/Impersonate/useImpersonationStore";
import { TaskListType } from "../Tasks/TasksRoutes";
import { useActiveTimeEntry } from "../Tasks/TimeEntry/useActiveTimeEntry";

export interface NavbarItem {
  key: string
  to: RouteLocationRaw
  icon: IconName
  label: string
}

const version = import.meta.env.VITE_APP_VERSION ?? (import.meta.env.DEV ? "dev" : pkgVersion);

/** State and actions shared by the desktop navbar and the phone tab bar. */
export function useNavbar() {
  const { t } = useI18n();
  const impersonationStore = useImpersonationStore();
  const locationStore = useLocationStore();
  const { theme } = useTheme();
  const { onError } = useServerErrorHandler();
  const router = useRouter();
  const route = useRoute();
  const {
    currentActiveTimeEntry,
    trackedTime,
    stopActiveTimeEntryInDialog,
  } = useActiveTimeEntry();
  const { user, userPreferences } = useAuthStore();

  const hasReportContext = computed(() => reportVoter.canCrud());
  const hasReservationContext = computed(() => reservationVoter.canCrud());
  const hasAutomationContext = computed(() => automationVoter.canCrud());
  const showReservationsModule = computed(() => userPreferences.value?.enableReservationModule === true);
  const showAutomationsModule = computed(() => userPreferences.value?.enableAutomationModule === true);

  const items = computed<NavbarItem[]>(() => {
    const result: NavbarItem[] = [];
    if (hasReportContext.value) {
      result.push({ key: "reports", to: { name: "reports" }, icon: "flag", label: t("navbar.reports") });
    }
    if (showReservationsModule.value && hasReservationContext.value) {
      result.push({ key: "reservations", to: { name: "reservations" }, icon: "calendar-days", label: t("navbar.reservations") });
    }
    result.push({ key: "tasks", to: { name: "tasks" }, icon: "clipboard-list", label: t("navbar.tasks") });
    if (showAutomationsModule.value && hasAutomationContext.value) {
      result.push({ key: "automations", to: { name: "automations" }, icon: "bot", label: t("navbar.automation") });
    }
    result.push({ key: "settings", to: { name: "settings" }, icon: "cog", label: t("navbar.settings") });
    return result;
  });

  function goToTimeEntry() {
    if (!currentActiveTimeEntry.value?.task) {
      return;
    }

    const taskId = currentActiveTimeEntry.value?.task.split("/").pop();
    router.replace({
      name: "task-list",
      params: { taskId: String(taskId) },
      query: route.name === "task-list" ? route.query : { view: TaskListType.TYPE_TODAY },
    });
  }

  function chooseLocation() {
    const from = { redirect: route.fullPath };
    return router.push({ name: "spaces", params: from, query: from });
  }

  const nextTheme = computed(() => {
    switch (theme.value) {
      case ThemeSetting.DARK:
        return ThemeSetting.LIGHT;
      case ThemeSetting.LIGHT:
        return ThemeSetting.DARK;
    }

    return ThemeSetting.DARK;
  });

  const themeSwitchLabel = computed(() => nextTheme.value === ThemeSetting.DARK ? t("base.switch_to_dark") : t("base.switch_to_light"));

  // The theme is a preference for every device; a failed save keeps the switch on this device and says so.
  function switchTheme() {
    chooseTheme(nextTheme.value).catch((error: unknown) => {
      onError({ error });
    });
  }

  async function clearImpersonation() {
    // The person being impersonated: the session runs as them until the header is dropped.
    const impersonatedUserId = user.value?.id ?? null;
    impersonationStore.reset();
    // Ends the session on that person's audit trail; sent as the staff member (the header is gone now).
    if (impersonatedUserId !== null) {
      await reportImpersonationStopped(impersonatedUserId);
    }
    await reloadSessionAfterIdentityChange();
    await router.push({ name: "tasks" });
  }

  function openDocs() {
    window.open("https://docs.verbleif.com", "_blank")?.focus();
  }

  function stopAndGoToTimeEntry() {
    stopActiveTimeEntryInDialog();
  }

  return {
    t,
    version,
    items,
    impersonationStore,
    locationStore,
    pendingInvites: locationStore.pendingInvites,
    currentActiveTimeEntry,
    trackedTime,
    goToTimeEntry,
    chooseLocation,
    switchTheme,
    clearImpersonation,
    openDocs,
    stopAndGoToTimeEntry,
    themeSwitchLabel,
  };
}
